import { gql } from '@apollo/client';
import { Alert, Button, Checkbox, Form, Select } from 'antd';
import FormLabel from 'components/FormLabel';
import MultilineInput from 'components/MultilineInput';
import FormDependency from 'components/FormDependency';
import { useMutation } from 'hooks/apollo';
import React from 'react';

import useUser from 'hooks/useUser';
import LicenseTag from 'components/LicenseTag';
import useDataSource from 'hooks/useDataSource';
import Page from 'components/Page';
import { PrivacySetting, TrackPageViews } from 'shared/services/prisma/types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import messages from '../messages';

const FIELDS = `
  id
  matomoId
  trackVisitors
  trackPageViews
  crashAnalytics
  formAnalytics
  mediaAnalytics
  linkTracking
  matomoSettings {
    id
    excludedIPs
    excludedParameters
    excludedUserAgents
    keepUrlFragment
    siteSearch
    siteSearchDefaultParameters
    siteSearchKeywordParameters
    siteSearchCategoryParameters
    ecommerce
  }
`;

const extraFields = `
  website {
    ${FIELDS}
  }
`;

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      ${FIELDS}
    }
  }
`;

const trackPageViewsOptions = [{
  value: TrackPageViews.Never,
  message: messages.trackPageViewsNever,
}, {
  value: TrackPageViews.PageLoad,
  message: messages.trackPageViewsPageLoad,
}, {
  value: TrackPageViews.UrlChange,
  message: messages.trackPageViewsUrlChange,
}];

const LicenseLimitedCheckbox = ({ activeLicense, setting, isSuperUser, label, description, value, onChange }) => {
  const canEdit = activeLicense?.settings?.[setting];
  return (
    <Checkbox disabled={!isSuperUser && !canEdit} checked={value} onChange={e => onChange(e.target.checked)}>
      <FormLabel
        label={label}
        description={description}
        extra={!canEdit && <LicenseTag activeLicense={activeLicense} setting={setting} showTooltip popoverProps={{ placement: 'left' }} />}
      />
    </Checkbox>
  );
};

const AnalyticsSettings = () => {
  const { dataSource, dataSourceId, loading } = useDataSource({ extraFields });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE);
  const { isSuperUser } = useUser();

  const { website } = dataSource || {};
  const websiteId = website?.id;

  const onSubmit = (values) => updateWebsite({ variables: { id: websiteId, input: values } });

  return (
    <Page loading={loading}>
      {website?.trackVisitors === PrivacySetting.Never ? (
        <Alert
          type="error"
          showIcon
          message="You have tracking turned off, turn it on in the privacy settings to start collecting analytics data."
          action={(
            <Link to={`/data-sources/${dataSourceId}/settings/privacy`}>
              <Button type="primary">Privacy settings</Button>
            </Link>
        )}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message="You can find settings for consent and cookies in the privacy settings."
          action={(
            <Link to={`/data-sources/${dataSourceId}/settings/privacy`}>
              <Button type="primary">Privacy settings</Button>
            </Link>
        )}
        />
      )}

      <Page.Content title="Tracking settings">
        <Form onFinish={onSubmit} initialValues={website} layout="vertical">
          <Form.Item name="trackPageViews" label={<FormLabel label={messages.trackPageViewsLabel} description={messages.trackPageViewsDescription} />}>
            <Select options={trackPageViewsOptions.map(o => ({ value: o.value, label: <FormattedMessage {...o.message} /> }))} />
          </Form.Item>

          <Form.Item
            name="linkTracking"
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.linkTrackingLabel} description={messages.linkTrackingDescription} />
            </Checkbox>
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'keepUrlFragment']}
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.keepUrlFragmentLabel} description={messages.keepUrlFragmentDescription} />
            </Checkbox>
          </Form.Item>
          <Form.Item name={['matomoSettings', 'ecommerce']}>
            <LicenseLimitedCheckbox activeLicense={dataSource?.activeLicense} setting="eCommerceAnalytics" isSuperUser={isSuperUser} label={messages.ecommerceLabel} description={messages.ecommerceDescription} />
          </Form.Item>
          <Form.Item name="crashAnalytics">
            <LicenseLimitedCheckbox activeLicense={dataSource?.activeLicense} setting="crashAnalytics" isSuperUser={isSuperUser} label={messages.crashAnalyticsLabel} description={messages.crashAnalyticsDescription} />
          </Form.Item>
          <Form.Item name="formAnalytics">
            <LicenseLimitedCheckbox activeLicense={dataSource?.activeLicense} setting="formAnalytics" isSuperUser={isSuperUser} label={messages.formAnalyticsLabel} description={messages.formAnalyticsDescription} />
          </Form.Item>
          <Form.Item name="mediaAnalytics">
            <LicenseLimitedCheckbox activeLicense={dataSource?.activeLicense} setting="mediaAnalytics" isSuperUser={isSuperUser} label={messages.mediaAnalyticsLabel} description={messages.mediaAnalyticsDescription} />
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'siteSearch']}
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.siteSearchLabel} description={messages.siteSearchDescription} />
            </Checkbox>
          </Form.Item>
          <FormDependency name={['matomoSettings', 'siteSearch']} show={v => v}>
            <Form.Item
              name={['matomoSettings', 'siteSearchDefaultParameters']}
              valuePropName="checked"
            >
              <Checkbox>
                <FormLabel label={messages.siteSearchDefaultParametersLabel} description={messages.siteSearchDefaultParametersDescription} />
              </Checkbox>
            </Form.Item>
          </FormDependency>
          <FormDependency name={['matomoSettings', 'siteSearchDefaultParameters']} show={v => !v}>
            <Form.Item
              name={['matomoSettings', 'siteSearchKeywordParameters']}
              label={<FormLabel label={messages.siteSearchKeywordParametersLabel} description={messages.siteSearchKeywordParametersDescription} />}
            >
              <MultilineInput placeholder={'Default:\nq, query, s, search, searchword, k, keyword, sok, searchQuery, searchquery, term, search, keywords, qry, keys '} />
            </Form.Item>
            <Form.Item
              name={['matomoSettings', 'siteSearchCategoryParameters']}
              label={<FormLabel label={messages.siteSearchCategoryParametersLabel} description={messages.siteSearchCategoryParametersDescription} />}
            >
              <MultilineInput placeholder={'Default:\ncat, qc, sc, category, kategori, c, facet, type, filter, CategoryId, kategoriID, field_category_target_id'} />
            </Form.Item>
          </FormDependency>
          <Form.Item
            name={['matomoSettings', 'excludedIPs']}
            label={<FormLabel label={messages.excludedIPsLabel} description={messages.excludedIPsDescription} />}
          >
            <MultilineInput placeholder={'Examples:\n1.2.3.4\n1.2.3.*'} />
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'excludedParameters']}
            label={<FormLabel label={messages.excludedParametersLabel} description={messages.excludedParametersDescription} />}
          >
            <MultilineInput placeholder={'Examples:\nsessionid\n/^sess.*|.*[dD]ate$/'} />
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'excludedUserAgents']}
            label={<FormLabel label={messages.excludedUserAgentsLabel} description={messages.excludedUserAgentsDescription} />}
          >
            <MultilineInput placeholder={'Examples:\n/bot|spider|crawl|scanner/'} />
          </Form.Item>
          <Page.Footer sticky>
            <Button type="primary" htmlType="submit" loading={updating}>Save</Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default AnalyticsSettings;
